import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Crown, MessageSquare, Calendar, TrendingUp } from 'lucide-react';

const SubscriptionLimits = () => {
  const [limits, setLimits] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLimits();
  }, []);

  const fetchLimits = async () => {
    try {
      const response = await api.get('/auth/limits');
      setLimits(response.data);
    } catch (error) {
      console.error('Error fetching limits:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !limits) {
    return null;
  }

  // Don't show for subscribed users
  if (limits.is_subscribed) {
    return (
      <Card className="border-green-200 bg-green-50" data-testid="subscription-active-card">
        <CardContent className="pt-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <Crown className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="font-semibold text-green-900">
                {limits.subscription_status === 'monthly' ? 'Monthly' : 'Yearly'} Subscription Active
              </p>
              <p className="text-sm text-green-700">Unlimited access to all features</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const messagePercent = (limits.messages.used / limits.messages.limit) * 100;
  const eventPercent = (limits.events.used / limits.events.limit) * 100;

  return (
    <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50" data-testid="subscription-limits-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-amber-600" />
              Free Plan Limits
            </CardTitle>
            <CardDescription>Upgrade to unlock unlimited features</CardDescription>
          </div>
          <Badge className="bg-amber-500 text-white">FREE</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Message Limit */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-gray-600" />
              <span className="font-medium">Organization Connections</span>
            </div>
            <span className="text-gray-600">
              {limits.messages.used} / {limits.messages.limit} used
            </span>
          </div>
          <Progress value={messagePercent} className="h-2" />
          <p className="text-xs text-gray-500">
            {limits.messages.remaining} connections remaining this month
          </p>
        </div>

        {/* Event Limit */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-gray-600" />
              <span className="font-medium">Events Created</span>
            </div>
            <span className="text-gray-600">
              {limits.events.used} / {limits.events.limit} used
            </span>
          </div>
          <Progress value={eventPercent} className="h-2" />
          <p className="text-xs text-gray-500">
            {limits.events.remaining} events remaining this year
          </p>
        </div>

        {/* Upgrade CTA */}
        <div className="pt-4 border-t border-amber-200">
          <Link to="/pricing">
            <Button className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white" data-testid="upgrade-button">
              <Crown className="h-4 w-4 mr-2" />
              Upgrade to Unlimited
            </Button>
          </Link>
          <p className="text-xs text-center text-gray-600 mt-2">
            Starting at just $3.70/month with yearly plan
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default SubscriptionLimits;
